import { ReactNode, useMemo } from 'react';
import { Table } from '@tanstack/react-table';

import { TableBody } from '@/components/ui/table';

import type { DataTableRow, TableAction } from '../types';
import { DataTableRow as TableRowComponent } from './data-table-row';
import { DataTableGroupRow } from './group-row';

interface HierarchicalTableBodyProps<T extends DataTableRow> {
  table: Table<T>;
  selectable?: boolean;
  actions?: TableAction<T>[];
  pinActions?: boolean;
  expandedRows?: Set<string>;
  onRowExpand?: (rowId: string, expanded: boolean) => void;
  getRowChildren?: (row: T) => T[];
  getRowHasChildren?: (row: T) => boolean;
  getRowLevel?: (row: T) => number;
  renderExpandIcon?: (row: T, isExpanded: boolean) => ReactNode;
  rowClassName?: (row: T) => string;
  isRowSelectable?: (row: T) => boolean;
  renderGroupRow?: (row: T, isExpanded: boolean, onToggle: () => void) => ReactNode;
  isGroupRow?: (row: T) => boolean;
}

interface HierarchicalRow extends DataTableRow {
  parentId?: string;
}

export function HierarchicalTableBody<T extends DataTableRow>({
  table,
  selectable = false,
  actions = [],
  pinActions = false,
  expandedRows,
  onRowExpand,
  getRowChildren,
  getRowHasChildren,
  getRowLevel,
  renderExpandIcon,
  rowClassName,
  isRowSelectable,
  renderGroupRow,
  isGroupRow
}: HierarchicalTableBodyProps<T>) {
  const visibleRows = useMemo(() => {
    const rows = table.getRowModel().rows;
    // Parent lookup by row id so visibility can walk the whole ancestor chain
    // instead of peeking a fixed number of levels up.
    const parentIdById = new Map<string, string | undefined>(
      rows.map((row) => [row.id, (row.original as HierarchicalRow).parentId])
    );

    /**
     * A row is visible only when EVERY ancestor is expanded. Walking the chain
     * (rather than checking the parent and grandparent) keeps arbitrarily deep
     * hierarchies correct — with a fixed depth check, rows below the deepest
     * handled level stayed visible after an ancestor collapsed.
     */
    const isVisible = (rowId: string): boolean => {
      const seen = new Set<string>([rowId]);
      let parentId = parentIdById.get(rowId);

      while (parentId) {
        if (!expandedRows?.has(parentId)) return false;
        // Defensive: a malformed parent chain must not spin forever
        if (seen.has(parentId)) return false;
        seen.add(parentId);
        parentId = parentIdById.get(parentId);
      }

      return true;
    };

    return rows.filter((row) => {
      const level = getRowLevel?.(row.original) || 0;
      if (level === 0) return true;

      // A non-root row with no parent link cannot be placed in the hierarchy
      if (!parentIdById.get(row.id)) return false;

      return isVisible(row.id);
    });
  }, [table, expandedRows, getRowLevel]);

  // Calculate total column count for colspan (columns + checkbox + actions)
  const totalColumns = table.getVisibleLeafColumns().length + (selectable ? 1 : 0) + (actions.length > 0 ? 1 : 0);

  return (
    <TableBody>
      {visibleRows.map((row) => {
        const rowData = row.original;
        const isExpanded = expandedRows?.has(row.id) || false;
        const level = getRowLevel?.(rowData) || 0;
        // `getRowHasChildren` wins when supplied so lazily-loaded rows can offer
        // an expand affordance before their children have been fetched.
        const hasChildren = getRowHasChildren
          ? getRowHasChildren(rowData)
          : getRowChildren
            ? (getRowChildren(rowData)?.length || 0) > 0
            : false;

        // Render group rows (e.g. pallet headers) as full-width colspan rows
        if (isGroupRow?.(rowData) && renderGroupRow) {
          const onToggle = () => onRowExpand?.(row.id, !isExpanded);
          return (
            <DataTableGroupRow key={row.id} colSpan={totalColumns} className={rowClassName?.(rowData)}>
              {renderGroupRow(rowData, isExpanded, onToggle)}
            </DataTableGroupRow>
          );
        }

        return (
          <TableRowComponent
            key={row.id}
            row={row}
            selectable={selectable && (isRowSelectable ? isRowSelectable(rowData) : true)}
            actions={actions}
            pinActions={pinActions}
            hierarchical={true}
            level={level}
            isExpanded={isExpanded}
            onToggleExpand={hasChildren && onRowExpand ? () => onRowExpand(row.id, !isExpanded) : undefined}
            renderExpandIcon={renderExpandIcon}
            rowClassName={rowClassName}
            hasChildren={hasChildren}
          />
        );
      })}
    </TableBody>
  );
}
